<template>
  <div class="video-page" :class="{ embed: isEmbed }">
    <!-- Loading -->
    <div v-if="loading" class="video-state">
      <div class="spinner" />
      <span>{{ t('common.loading') }}</span>
    </div>

    <!-- Not available -->
    <div v-else-if="errorKind" class="video-state">
      <h1>{{ t(`video.${errorKind}Title`) }}</h1>
      <p>{{ t(`video.${errorKind}Desc`) }}</p>
      <a v-if="errorKind === 'processing'" href="#" class="video-link" @click.prevent="load">{{ t('video.refresh') }}</a>
      <a v-else href="https://virtualtoureasy.com" class="video-link">{{ t('channel.backHome') }} →</a>
    </div>

    <!-- Embedding not included in this owner's plan: the page also sends frame-ancestors 'self' -->
    <div v-else-if="video && isEmbed && !embeddable" class="video-state">
      <p class="state-title">{{ video.title || t('video.untitled') }}</p>
      <a :href="video.share_url" target="_blank" rel="noopener" class="video-link">{{ t('video.watchOn') }} →</a>
    </div>

    <!-- Player -->
    <template v-else-if="video">
      <!-- No logo up here: the company identity lives in the channel row's avatar below -->
      <header v-if="!isEmbed" class="video-header">
        <div class="header-text">
          <h1 class="video-title">{{ video.title || t('video.untitled') }}</h1>
          <p class="video-meta">
            <span v-if="branding?.company_name">{{ t('video.by', { name: branding.company_name }) }} · </span>
            <span>{{ formattedDate }}</span>
          </p>
        </div>
      </header>

      <div class="player-wrap" :style="{ maxWidth: playerMaxWidth }">
        <div class="player-box" :style="{ aspectRatio: aspectCss }">
          <video
            ref="player"
            :src="video.url || undefined"
            :poster="video.poster_url || undefined"
            controls
            playsinline
            preload="metadata"
            :autoplay="autoplay"
            :muted="autoplay"
            class="player"
            controlslist="nodownload noremoteplayback"
            disablepictureinpicture
            @play="onPlay"
            @ended="onEnded"
            @timeupdate="onTimeUpdate"
            @contextmenu.prevent="openContextMenu"
          />
          <!-- YouTube-style right-click menu: org identity + link instead of the browser's save menu -->
          <div v-if="ctxMenu.open" class="ctx-menu" :style="{ left: ctxMenu.x + 'px', top: ctxMenu.y + 'px' }" @click.stop>
            <a v-if="channel" :href="channel.url" class="ctx-item ctx-org">{{ channel.name || channel.handle }}</a>
            <span v-else-if="branding?.company_name" class="ctx-item ctx-org">{{ branding.company_name }}</span>
            <button type="button" class="ctx-item" @click="copyVideoLink">{{ ctxCopied ? '✓ ' : '' }}{{ t('video.copyLink') }}</button>
            <a href="https://virtualtoureasy.com" target="_blank" rel="noopener" class="ctx-item">VirtualTourEasy</a>
          </div>
          <img
            v-if="branding?.logo_url && showOverlayLogo"
            :src="branding.logo_url"
            alt=""
            class="overlay-logo watermark"
            :class="logoPositionClass"
          />
          <a v-else-if="poweredBy" href="https://virtualtoureasy.com" target="_blank" rel="noopener" class="vte-watermark">VirtualTourEasy</a>
          <div v-if="showTitleOverlay" class="lower-third">{{ video.title }}</div>
          <button v-if="autoplay && muted" type="button" class="unmute" @click="unmute">🔇 {{ t('video.unmute') }}</button>
          <a v-if="isEmbed && tour?.viewer_url" :href="tour.viewer_url" target="_blank" rel="noopener" class="embed-chip">360° →</a>
        </div>
      </div>

      <!-- Action bar: channel identity left (the company logo avatar IS the channel link —
           no separate "View channel" button), the virtual-tour CTA centered, share right. -->
      <section v-if="!isEmbed" class="channel-row" :class="{ 'no-channel': !channel }" :style="{ maxWidth: playerMaxWidth }">
        <a v-if="channel" :href="channel.url" class="channel-id">
          <img v-if="branding?.favicon_url || branding?.logo_url" :src="branding.favicon_url || branding.logo_url" alt="" class="channel-avatar" />
          <span v-else class="channel-avatar channel-avatar-letter">{{ (channel.name || channel.handle).charAt(0).toUpperCase() }}</span>
          <span class="channel-names">
            <span class="channel-name">{{ channel.name || channel.handle }}</span>
            <span class="channel-handle">@{{ channel.handle }}</span>
          </span>
        </a>
        <div class="bar-center">
          <a v-if="tour?.viewer_url" :href="tour.viewer_url" class="btn-primary">{{ t('video.exploreTour') }} →</a>
        </div>
        <div class="bar-actions">
          <button type="button" class="btn-icon" :title="t('common.share')" :aria-label="t('common.share')" @click="sharePanelOpen = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.5" x2="15.4" y2="6.5"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/></svg>
          </button>
        </div>
      </section>

      <footer v-if="!isEmbed && poweredBy" class="powered-by video-footer">
        <a href="https://virtualtoureasy.com" target="_blank" rel="noopener">Powered by <strong>VirtualTourEasy</strong></a>
      </footer>

      <SharePanel
        :open="sharePanelOpen"
        :tour-title="video.title || t('video.untitled')"
        :tour-id="video.uuid"
        :share-url="shareUrl"
        :embed-code="embedCode"
        :show-embed="embeddable"
        :tracker="shareTracker"
        @close="sharePanelOpen = false"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import SharePanel from './SharePanel.vue'
import { setVideoMeta } from '../composables/useSeoMeta'
import { AnalyticsTracker } from '../lib/analytics'

interface PublicVideo {
  uuid: string
  tour_uuid: string
  title: string | null
  description: string | null
  url: string | null
  poster_url: string | null
  download_url: string | null
  share_url: string
  embed_url: string
  mime: string | null
  aspect: string | null
  width: number | null
  height: number | null
  duration_s: number | null
  created_at: string
  /** per-video "show my logo" toggle from the generation form (default true) */
  show_logo?: boolean
}

interface PublicTour {
  uuid: string
  ref_id: string | null
  title: string | null
  description: string | null
  viewer_url: string
}

const props = defineProps<{ videoUuid: string; apiUrl: string }>()
const { t, locale } = useI18n()

const loading = ref(true)
const errorKind = ref<'notFound' | 'unpublished' | 'processing' | 'suspended' | null>(null)
const video = ref<PublicVideo | null>(null)
const tour = ref<PublicTour | null>(null)
const branding = ref<any>(null)
const channel = ref<{ name: string | null; handle: string; url: string } | null>(null)
const embeddable = ref(true)
const player = ref<HTMLVideoElement | null>(null)
const sharePanelOpen = ref(false)
const ctxMenu = ref({ open: false, x: 0, y: 0 })
const ctxCopied = ref(false)

function openContextMenu(event: MouseEvent) {
  const box = (event.currentTarget as HTMLElement).parentElement?.getBoundingClientRect()
  ctxMenu.value = {
    open: true,
    x: box ? Math.min(event.clientX - box.left, box.width - 200) : event.offsetX,
    y: box ? Math.min(event.clientY - box.top, box.height - 120) : event.offsetY,
  }
  ctxCopied.value = false
}

function closeContextMenu() {
  ctxMenu.value = { ...ctxMenu.value, open: false }
}

async function copyVideoLink() {
  try { await navigator.clipboard.writeText(shareUrl.value); ctxCopied.value = true } catch { /* clipboard denied */ }
  window.setTimeout(closeContextMenu, 600)
}
const muted = ref(false)
const showTitleOverlay = ref(false)
const playedTracked = ref(false)
const completeTracked = ref(false)
let tracker: AnalyticsTracker | null = null
let retryTimer: number | null = null
let retries = 0

const params = new URLSearchParams(window.location.search)
const isEmbed = params.get('embed') === '1' || window.self !== window.top
const autoplay = isEmbed || params.get('autoplay') === '1'

const aspectRatio = computed(() => {
  const [w, h] = String(video.value?.aspect || '16:9').split(':').map(Number)
  return w && h ? w / h : 16 / 9
})
const aspectCss = computed(() => `${aspectRatio.value}`)
const playerMaxWidth = computed(() => (aspectRatio.value < 1 ? '420px' : aspectRatio.value < 1.2 ? '640px' : '1100px'))
const poweredBy = computed(() => branding.value?.powered_by_enabled !== false)
// Watermark = branded logo, but only when this specific video kept "show my logo" on
const showOverlayLogo = computed(() => !!branding.value?.logo_url && video.value?.show_logo !== false)
const logoPositionClass = computed(() => `pos-${branding.value?.logo_position || 'top-right'}`)
const shareUrl = computed(() => video.value?.share_url || window.location.href)
const embedCode = computed(() => {
  if (!video.value) return ''
  const padding = (100 / aspectRatio.value).toFixed(2)
  const maxWidth = aspectRatio.value < 1 ? 'max-width:420px;' : ''
  return `<div style="position:relative;${maxWidth}padding-bottom:${padding}%;height:0;overflow:hidden;border-radius:12px"><iframe src="${video.value.embed_url}" title="${(video.value.title || 'Video').replace(/"/g, '&quot;')}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen loading="lazy"></iframe></div>`
})
const formattedDate = computed(() => {
  if (!video.value?.created_at) return ''
  try { return new Date(`${video.value.created_at.replace(' ', 'T')}Z`).toLocaleDateString(locale.value) } catch { return '' }
})
const shareTracker = computed(() => (tracker ? { trackShare: (platform: string) => tracker!.trackShare(platform) } : null))

function applyBranding(b: any) {
  const root = document.documentElement
  const colors = b?.colors
  if (colors?.primary) root.style.setProperty('--brand-primary', colors.primary)
  if (colors?.background) root.style.setProperty('--brand-bg', colors.background)
  if (colors?.text) root.style.setProperty('--brand-text', colors.text)
  if (b?.viewer_font) {
    const fontId = `brand-font-${String(b.viewer_font).replace(/\s+/g, '-')}`
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link')
      link.id = fontId
      link.rel = 'stylesheet'
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(b.viewer_font)}:wght@400;500;600;700&display=swap`
      document.head.appendChild(link)
    }
    root.style.setProperty('--brand-font', `'${b.viewer_font}', sans-serif`)
  }
  if (b?.favicon_url) {
    let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link) }
    link.href = b.favicon_url
  }
}

async function load() {
  loading.value = true
  errorKind.value = null
  try {
    const viewParams = new URLSearchParams()
    if (isEmbed) {
      viewParams.set('embed', '1')
      if (document.referrer) viewParams.set('host', document.referrer.slice(0, 500))
    } else if (document.referrer) {
      viewParams.set('ref', document.referrer.slice(0, 500))
    }
    const qs = viewParams.toString()
    const res = await fetch(`${props.apiUrl}/viewer/video/${encodeURIComponent(props.videoUuid)}${qs ? `?${qs}` : ''}`, { headers: { Accept: 'application/json' } })
    if (res.status === 404) { errorKind.value = 'notFound'; return }
    const data = await res.json()
    if (data.suspended) { errorKind.value = 'suspended'; return }
    if (data.unpublished) { errorKind.value = 'unpublished'; return }
    if (data.processing) {
      errorKind.value = 'processing'
      if (retries < 6) { retries++; retryTimer = window.setTimeout(load, 10_000) }
      return
    }
    if (!data.video) { errorKind.value = 'notFound'; return }
    video.value = data.video
    tour.value = data.tour
    branding.value = data.branding || null
    channel.value = data.channel || null
    embeddable.value = data.embeddable !== false
    applyBranding(branding.value)
    setVideoMeta({
      title: video.value!.title,
      description: tour.value?.description || video.value!.description,
      image: video.value!.poster_url,
      url: video.value!.share_url,
      orgName: branding.value?.company_name || null,
      video: { url: video.value!.url, mime: video.value!.mime, width: video.value!.width, height: video.value!.height, embedUrl: video.value!.embed_url },
    })
    if (!tracker && video.value?.tour_uuid) tracker = new AnalyticsTracker(props.apiUrl, video.value.tour_uuid)
    if (video.value?.title) {
      showTitleOverlay.value = true
      window.setTimeout(() => { showTitleOverlay.value = false }, 4500)
    }
    muted.value = autoplay
  } catch {
    errorKind.value = 'notFound'
  } finally {
    loading.value = false
  }
}

function onPlay() {
  if (!playedTracked.value && video.value) {
    playedTracked.value = true
    tracker?.trackVideo('video_play', video.value.uuid)
  }
}

function onEnded() {
  if (!completeTracked.value && video.value) {
    completeTracked.value = true
    tracker?.trackVideo('video_complete', video.value.uuid)
  }
}

function onTimeUpdate() {
  const el = player.value
  if (!el || completeTracked.value || !el.duration) return
  if (el.currentTime / el.duration >= 0.95) onEnded()
}


function unmute() {
  if (player.value) {
    player.value.muted = false
    muted.value = false
    void player.value.play().catch(() => {})
  }
}

onMounted(() => {
  load()
  document.addEventListener('click', closeContextMenu)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', closeContextMenu)
  if (retryTimer) window.clearTimeout(retryTimer)
  tracker?.destroy()
})
</script>

<style scoped>
.video-page {
  min-height: 100vh;
  background: var(--brand-bg, #0b0b0f);
  color: var(--brand-text, #f5f5f7);
  font-family: var(--brand-font, 'Nunito', system-ui, sans-serif);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px 40px;
  gap: 20px;
}
.video-page.embed {
  padding: 0;
  gap: 0;
  min-height: 100vh;
  justify-content: center;
  background: #000;
}
.video-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 60vh;
  text-align: center;
}
.video-state h1 { font-size: 1.4rem; margin: 0; }
.video-state p { margin: 0; opacity: 0.7; }
.video-link { color: var(--brand-primary, #3b82f6); }
.spinner {
  width: 28px; height: 28px; border-radius: 50%;
  border: 3px solid rgba(255,255,255,0.2); border-top-color: var(--brand-primary, #3b82f6);
  animation: spin 0.9s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.video-header {
  width: 100%;
  max-width: 1100px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.video-title { font-size: 1.35rem; margin: 0; }
.video-meta { margin: 2px 0 0; font-size: 0.85rem; opacity: 0.7; }
.player-wrap { width: 100%; }
.embed .player-wrap { max-width: none !important; height: 100vh; }
.player-box {
  position: relative;
  width: 100%;
  background: #000;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
}
.embed .player-box { border-radius: 0; height: 100%; aspect-ratio: auto !important; box-shadow: none; }
.player { position: absolute; inset: 0; width: 100%; height: 100%; background: #000; }
.overlay-logo {
  position: absolute;
  height: 34px; max-width: 130px; object-fit: contain;
  pointer-events: none; opacity: 0.92;
  filter: drop-shadow(0 1px 3px rgba(0,0,0,0.6));
}
.pos-top-right { top: 12px; right: 12px; }
.pos-top-left { top: 12px; left: 12px; }
.pos-bottom-right { bottom: 56px; right: 12px; }
.pos-bottom-left { bottom: 56px; left: 12px; }
.lower-third {
  position: absolute; left: 16px; bottom: 64px;
  padding: 6px 12px; border-radius: 8px;
  background: rgba(0,0,0,0.55); color: #fff; font-weight: 600; font-size: 0.95rem;
  backdrop-filter: blur(6px);
  animation: fadeOut 4.5s forwards;
  pointer-events: none;
}
@keyframes fadeOut { 0%, 70% { opacity: 1; } 100% { opacity: 0; } }
.unmute {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  border: 0; border-radius: 999px; padding: 10px 18px; font-weight: 600; cursor: pointer;
  background: rgba(255,255,255,0.92); color: #111;
}
.embed-chip {
  position: absolute; right: 10px; bottom: 10px;
  font-size: 12px; font-weight: 700; text-decoration: none;
  background: rgba(0,0,0,0.6); color: #fff; padding: 4px 8px; border-radius: 999px;
}
.ctx-menu { position: absolute; z-index: 30; min-width: 190px; background: rgba(15,23,42,0.97); border: 1px solid rgba(148,163,184,0.2); border-radius: 10px; padding: 6px; box-shadow: 0 12px 40px rgba(0,0,0,0.5); }
.ctx-item { display: block; width: 100%; padding: 7px 10px; border: 0; border-radius: 6px; background: transparent; color: #e2e8f0; font-size: 13px; text-align: left; text-decoration: none; cursor: pointer; }
.ctx-item:hover { background: rgba(148,163,184,0.12); }
.ctx-org { font-weight: 600; border-bottom: 1px solid rgba(148,163,184,0.15); border-radius: 6px 6px 0 0; }
.overlay-logo.watermark { opacity: 0.55; max-height: 34px; pointer-events: none; }
.vte-watermark { position: absolute; right: 12px; top: 10px; color: rgba(255,255,255,0.55); font-size: 12px; font-weight: 700; letter-spacing: 0.02em; text-decoration: none; text-shadow: 0 1px 4px rgba(0,0,0,0.5); }
.vte-watermark:hover { color: rgba(255,255,255,0.85); }

.channel-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  width: 100%;
  margin: 16px auto 0;
  padding: 0 4px;
}
.channel-row.no-channel { justify-content: center; }
/* CTA centered between the channel identity (left) and share (right) */
.bar-center { flex: 1; display: flex; justify-content: center; min-width: 0; }
.channel-row.no-channel .bar-center { flex: 0 1 auto; }
.bar-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.btn-icon {
  display: flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; border-radius: 10px; cursor: pointer;
  background: rgba(255,255,255,0.08); color: inherit; border: 1px solid rgba(255,255,255,0.15);
}
.btn-icon:hover { background: rgba(255,255,255,0.15); }
.channel-id { display: flex; align-items: center; gap: 10px; text-decoration: none; min-width: 0; }
.channel-avatar {
  width: 40px; height: 40px; border-radius: 50%; object-fit: cover; flex-shrink: 0;
  background: #1e293b;
}
.channel-avatar-letter {
  display: flex; align-items: center; justify-content: center;
  color: #e2e8f0; font-weight: 700; font-size: 18px;
}
.channel-names { display: flex; flex-direction: column; min-width: 0; }
.channel-name { color: #f1f5f9; font-weight: 600; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.channel-handle { color: #94a3b8; font-size: 12px; }

.video-actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.btn-primary, .btn-secondary {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; font-weight: 600; text-decoration: none; cursor: pointer;
  border: 1px solid transparent; font: inherit;
}
.btn-primary { background: var(--brand-primary, #3b82f6); color: #fff; }
.btn-secondary { background: rgba(255,255,255,0.08); color: inherit; border-color: rgba(255,255,255,0.15); }
.btn-secondary:hover { background: rgba(255,255,255,0.14); }
.video-footer { font-size: 12px; opacity: 0.6; }
.video-footer a { color: inherit; text-decoration: none; }
</style>
